import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersist from 'vuex-persist';
import DataService from '@/services/data-service';
import CryptoService from '@/services/crypto-services';
import { RootState } from './Types/RootState';

const dataService = new DataService();

Vue.use(Vuex);

const vuexPersist = new VuexPersist({
  key: 'my-app',
  storage: window.localStorage,
});

export default new Vuex.Store<RootState>({
  state: {
    Gateway: '',
    AgentId: '',
    SessionID: '',
    TenantId: '',
    userEmail: '',
    Password: '',
    LinkLabels: {},
    User: {
      IsAuthenticated: false,
      AuthToken: '',
      TokenExpireIn: null,
      AdminRights: false,
      AppRights: false,
      Username: '',
      FirstName: '',
      LastName: '',
      UserId: '',
      Permissions: [],
    },
  },
  getters: {
    getGateway(state) { return state.Gateway },
    getAgentInfo(state) {
      return state;
    },
    getUserPermissions(state) {
      if (state.User.Permissions && state.User.Permissions.length > 0) { return state.User.Permissions.map(x => CryptoService.decryptString(x)); }
      return [];
    },
    getIsAuthenticated(state) {
      return state.User.IsAuthenticated;
    },
    getAuthToken(state) {
      if (state.User.AuthToken && state.User.AuthToken.length > 0) { return CryptoService.decryptString(state.User.AuthToken); }
      return '';
    },
    getAdminRights(state) {
      return state.User.AdminRights;
    },
    getAppRights(state) {
      return state.User.AppRights;
    },
    getUserFullName(state) {
      return `${state.User.FirstName} ${state.User.LastName}`;
    },
    getUsername(state) {
      return state.User.Username;
    },
    getUserId(state) {
      return state.User.UserId;
    },
    getDefaultRoute(state, getters) {
      const info = {
        name: '',
        route: '',
      };
      if (getters.IsValidCode('AH1AG1312')) {
        info.name = 'Agents';
        info.route = 'controlboard';
      } else if (getters.IsValidCode('AH1AR1110')) {
        info.name = 'Approve Release';
        info.route = 'approverelease';
      } else if (getters.IsValidCode('AH1LG1101')) {
        info.name = 'Logs';
        info.route = 'logs';
      } else if (getters.IsValidCode('AH1US0901')) {
        info.name = 'Users';
        info.route = 'users';
      } else if (getters.IsValidCode('AH1PR0106')) {
        info.name = 'Personas';
        info.route = 'personas';
      } else if (getters.IsValidCode('AH1DP1302')) {
        info.name = 'Developer Board';
        info.route = '';
      }
      return info;
    },
    getDefaultAdminRoute(state, getters) {
      const info = {
        name: '',
        route: '',
      };
      if (getters.IsValidCode('AH1US0901')) {
        info.name = 'Users';
        info.route = 'users';
      } else if (getters.IsValidCode('AH1PR0106')) {
        info.name = 'Personas';
        info.route = 'personas';
      } else if (getters.IsValidCode('AH1AU0013')) {
        info.name = 'Audit';
        info.route = 'audit';
      }
      return info;
    },
    getDefaultAppRoute(state, getters) {
      const info = {
        name: '',
        route: '',
      };
      if (getters.IsValidCode('AH1AG1312')) {
        info.name = 'Agents';
        info.route = 'controlboard';
      } else if (getters.IsValidCode('AH1AR1110')) {
        info.name = 'Approve Release';
        info.route = 'approverelease';
      } else if (getters.IsValidCode('AH1LG1101')) {
        info.name = 'Logs';
        info.route = 'logs';
      }
      return info;
    },
    IsValidCode: state => (code) => {
      if (state.User.Permissions && state.User.Permissions.length > 0) {
        return state.User.Permissions.findIndex(p => CryptoService.decryptString(p) === code) !== -1;
      }
      return false;
    },
    getLinkLabels(state) {
      return state.LinkLabels;
    },
  },
  mutations: {
    /* eslint-disable */
    setGatewayInfo(state, payload) {
      state.Gateway = payload;
    },
    setAgentInfo(state, payload) {
      state.AgentId = payload.AgentId;
      state.SessionID = payload.SessionID;
      state.TenantId = payload.TenantId;
    },
    setUserInfo(state, payload) {
      state.User.IsAuthenticated = payload.IsAuthenticated;
      state.User.AuthToken = CryptoService.encryptString(payload.AuthToken);
      state.User.TokenExpireIn = payload.TokenExpireIn;
      state.User.UserId = payload.UserId;
      state.User.Username = payload.Username;
      state.User.FirstName = payload.FirstName;
      state.User.LastName = payload.LastName;
      state.User.Permissions = payload.Permissions;
      if (payload.Permissions && payload.Permissions.length > 0) {
        payload.Permissions.forEach(p => {
          let code = CryptoService.decryptString(p);
          if (code === 'AH1PR0106' || code === 'AH1US0901' || code === 'AH1AU0013') {
            state.User.AdminRights = true;
          } else if (code === 'AH1AG1312' || code === 'AH1AR1110' || code === 'AH1DP1302' || code === 'AH1LG1101') {
            state.User.AppRights = true;
          }
        }) !== -1;
      }
    },
    setLinkLabels(state, payload) {
      state.LinkLabels = payload;
    },
    removeUserInfo(state) {
      state.User.IsAuthenticated = false;
      state.User.AuthToken = '';
      state.User.TokenExpireIn = null;
      state.User.AdminRights = false;
      state.User.AppRights = false;
      state.User.UserId = '';
      state.User.Username = '';
      state.User.FirstName = '';
      state.User.LastName = '';
      state.User.Permissions = [];
    },
    initialiseStore(state) {
      // Check if the ID exists
      if (localStorage.getItem('store')) {
        this.replaceState(Object.assign(state, JSON.parse(localStorage.getItem('store'))));
      }
    },
    /* eslint-enable */
  },
  actions: {
    login(context, payload): Promise<boolean> {
      return new Promise((resolve, reject) => {
        dataService
          .login(payload.username, payload.password)
          .then((response) => {
            if (response.data && response.data.access_token) {
              dataService.getUser(response.data.access_token)
                .then((userResp) => {
                  if (userResp.data && userResp.data.User) {
                    resolve(true);
                    context.commit('setUserInfo', {
                      IsAuthenticated: true,
                      AuthToken: response.data.access_token,
                      TokenExpireIn: response.data.expires_in,
                      UserId: userResp.data.User.UserId,
                      Username: userResp.data.User.Username,
                      FirstName: userResp.data.User.FirstName,
                      LastName: userResp.data.User.LastName,
                      Permissions: userResp.data.User.Permissions,
                    });
                    context.commit('setLinkLabels', userResp.data.LinkLabels);
                  } else {
                    context.commit('removeUserInfo');
                    reject(userResp);
                  }
                })
                .catch(((userError) => {
                  context.commit('removeUserInfo');
                  reject(userError);
                }));
            } else {
              context.commit('removeUserInfo');
              reject(response);
            }
          })
          .catch((error) => {
            context.commit('removeUserInfo');
            reject(error);
          });
      });
    },
    logout(context): Promise<boolean> {
      return new Promise<boolean>((resolve, reject) => {
        dataService.logout()
          .then((response) => {
            context.commit('removeUserInfo');
            resolve(!!response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
  },
  plugins: [vuexPersist.plugin],
});
